<script setup>
import { computed, ref, watch } from 'vue'

import { castIronData, castIronWatts } from '../lib/cast-iron-radiator'
import { trackEvent } from '../lib/tracking'

const props = defineProps({
  strings: {
    type: Object,
    default: null,
  },
  market: {
    type: String,
    default: '',
  },
})

const typeValue = ref('')
const elementsValue = ref(2)
const flowTemp = ref(75)
const returnTemp = ref(65)
const roomTemp = ref(20)

const copy = computed(() => props.strings?.castIronRadiator ?? {})

const elementsRange = computed(() => {
  const items = []
  for (let i = castIronData.minElements; i <= castIronData.maxElements; i++) {
    items.push(i)
  }
  return items
})

const watts = computed(() => {
  if (!typeValue.value) return 0
  return castIronWatts(
    typeValue.value,
    Number(elementsValue.value),
    Number(flowTemp.value),
    Number(returnTemp.value),
    Number(roomTemp.value)
  )
})

watch(watts, (val) => {
  if (val > 0) {
    trackEvent('calculator_cast_iron_result', {
      type: typeValue.value,
      elements: elementsValue.value,
      flow_temp: flowTemp.value,
      return_temp: returnTemp.value,
      room_temp: roomTemp.value,
      watts: val,
      market: props.market,
    })
  }
})
</script>

<template>
  <section class="cast-iron-radiator">
    <p class="cast-iron-radiator__description">
      {{ copy.description || 'Calculate heat output for cast iron column radiators.' }}
    </p>
    <div class="cast-iron-radiator__fields">
      <label class="cast-iron-radiator__field">
        <span class="cast-iron-radiator__label">{{ copy.type || 'Radiator Type' }}</span>
        <span class="cast-iron-radiator__select">
          <select v-model="typeValue">
            <option value="" disabled>{{ copy.select || 'Select' }}</option>
            <option v-for="type in castIronData.types" :key="type" :value="type">
              {{ type }}
            </option>
          </select>
        </span>
      </label>
      <label class="cast-iron-radiator__field">
        <span class="cast-iron-radiator__label">{{ copy.elements || 'Number of Elements' }}</span>
        <span class="cast-iron-radiator__select">
          <select v-model="elementsValue">
            <option v-for="n in elementsRange" :key="n" :value="n">
              {{ n }}
            </option>
          </select>
        </span>
      </label>
      <label class="cast-iron-radiator__field">
        <span class="cast-iron-radiator__label">{{ copy.flowTemp || 'Flow Temperature' }}</span>
        <span class="cast-iron-radiator__input-wrap">
          <input v-model.number="flowTemp" type="number" min="1" max="120" step="1" />
          <span class="cast-iron-radiator__input-unit">C°</span>
        </span>
      </label>
      <label class="cast-iron-radiator__field">
        <span class="cast-iron-radiator__label">{{ copy.returnTemp || 'Return Temperature' }}</span>
        <span class="cast-iron-radiator__input-wrap">
          <input v-model.number="returnTemp" type="number" min="1" max="120" step="1" />
          <span class="cast-iron-radiator__input-unit">C°</span>
        </span>
      </label>
      <label class="cast-iron-radiator__field">
        <span class="cast-iron-radiator__label">{{ copy.roomTemp || 'Room Temperature' }}</span>
        <span class="cast-iron-radiator__input-wrap">
          <input v-model.number="roomTemp" type="number" min="0" max="40" step="1" />
          <span class="cast-iron-radiator__input-unit">C°</span>
        </span>
      </label>
    </div>

    <p class="cast-iron-radiator__result-label">
      {{ copy.resultLabel || 'Heat output' }}
    </p>
    <div class="cast-iron-radiator__result">
      <span class="cast-iron-radiator__value">{{ watts }}</span>
      <span class="cast-iron-radiator__unit">W</span>
    </div>
  </section>
</template>

<style scoped>
.cast-iron-radiator {
  display: grid;
  gap: 16px;
  padding: 16px 0;
  border-top: 1px solid #e4eaed;
}

.cast-iron-radiator__description {
  margin: 0;
  font-size: 0.85rem;
  color: #6b7b7f;
}

.cast-iron-radiator__fields {
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr;
}

.cast-iron-radiator__field {
  display: grid;
  gap: 6px;
  font-size: 0.85rem;
  color: #4e6b6e;
}

.cast-iron-radiator__label {
  font-weight: 600;
}

.cast-iron-radiator__select select {
  width: 100%;
  height: 36px;
  padding: 8px 40px 8px 10px;
  border: 1px solid var(--semantic-border-muted);
  border-radius: 2px;
  background-color: var(--semantic-bg-default);
  color: var(--semantic-text-default);
  outline: none;
  font-size: 0.85rem;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath d='M7 10l5 5 5-5' fill='none' stroke='%23888' stroke-width='2'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 18px 18px;
}

.cast-iron-radiator__input-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}

.cast-iron-radiator__input-wrap input {
  width: 100%;
  height: 36px;
  padding: 8px 10px;
  border: 1px solid var(--semantic-border-muted);
  border-radius: 2px;
  outline: none;
  background-color: var(--semantic-bg-default);
  color: var(--semantic-text-default);
  font-size: 0.85rem;
}

.cast-iron-radiator__input-unit {
  font-size: 0.8rem;
  color: #607276;
  white-space: nowrap;
}

.cast-iron-radiator__result {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border: 1px solid var(--semantic-border-muted);
  border-radius: 2px;
  background: var(--semantic-bg-default);
  font-weight: 700;
  color: #0f2b2f;
  width: fit-content;
  margin: 8px auto 0;
}

.cast-iron-radiator__result-label {
  margin: var(--space-64) 0 0;
  font-size: 0.8rem;
  font-weight: 500;
  color: #6b7b7f;
  text-align: center;
}

.cast-iron-radiator__value {
  font-size: 1.2rem;
}

.cast-iron-radiator__unit {
  font-size: 0.8rem;
  color: #607276;
}
</style>
